import { ReactNode } from 'react';
import ReactModal, { Styles } from 'react-modal';

import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import ModalCloseButton from './modal-close-button';
import { BadgeStyled } from 'approot/shared/badge/badge.style';
import {
  SimpleModalHeaderStyled,
  SimpleModalInfoContainerStyled,
  SimpleModalOffsetContainer,
  SimpleModalScrollable,
  SimpleModalTitleSpan,
  SimpleModalTitleStyled,
} from './simple-modal.styles';
import { useSimpleModal } from './simple-modal.hooks';
import { ModalBaseCss, SIZE_STANDARD } from './modal.constants';

export type Props = {
  title: string;
  titleElement?: ReactNode;
  size?: number;
  children?: ReactNode;
  isVisible: boolean;
  isCloseButtonVisible?: boolean;
  dataTracking?: {
    label?: string;
    category?: string;
    values?: { [key: string]: any };
  };
  dataPushOnClose?: boolean;
  onClose?: () => void;
};

// Otherwise tests throw an error about not finding #root
if (process.env.NODE_ENV !== 'test' && document.getElementById('root'))
  ReactModal.setAppElement('#root');

const SimpleModalInfo = ({
  title,
  titleElement,
  size = SIZE_STANDARD,
  children,
  isVisible,
  isCloseButtonVisible,
  dataTracking,
  dataPushOnClose = true,
  onClose,
}: Props) => {
  const isMediaUpSm = useMediaQuery(MEDIA_SM);

  const { handleClose, handleAfterOpen } = useSimpleModal({
    dataTracking,
    dataPushOnClose,
    onClose,
  });

  const style: Styles = {
    ...ModalBaseCss,
    overlay: {
      ...ModalBaseCss.overlay,
      overflow: 'hidden',
    },
    content: {
      ...ModalBaseCss.content,
      maxHeight: '85vh',
      maxWidth: size ? `${size}px` : 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
  };

  if (!isMediaUpSm && style.content) {
    style.content.height = '100vh';
    style.content.maxHeight = '100%';
    style.content.maxWidth = '100%';
    style.content.inset = 'auto';
    style.content.margin = '0';
    style.content.padding = '0';
  }

  return (
    <ReactModal
      isOpen={isVisible}
      onAfterOpen={handleAfterOpen}
      onRequestClose={handleClose}
      style={style}
      contentLabel={title}
      shouldCloseOnOverlayClick={isCloseButtonVisible}
      bodyOpenClassName="ReactModal__Body--open"
    >
      <SimpleModalInfoContainerStyled
        data-selector="react-modal-window"
        isCloseButtonVisible={isCloseButtonVisible !== false}
      >
        <SimpleModalHeaderStyled>
          {titleElement ? (
            titleElement
          ) : (
            <SimpleModalTitleStyled data-selector="modal-heading">
              {title}
            </SimpleModalTitleStyled>
          )}
          {isCloseButtonVisible !== false && (
            <ModalCloseButton
              onClick={handleClose}
              data-selector="close-modal"
              aria-label={`Close ${title}`}
            />
          )}
        </SimpleModalHeaderStyled>
        <SimpleModalScrollable>
          <SimpleModalOffsetContainer>{children}</SimpleModalOffsetContainer>
        </SimpleModalScrollable>
      </SimpleModalInfoContainerStyled>
    </ReactModal>
  );
};

export default SimpleModalInfo;

export const SimpleModalInfoTitle = ({
  title,
  badgeTitle,
  badgeColour,
}: {
  title: string;
  badgeTitle?: string;
  badgeColour?: string;
}) => (
  <SimpleModalTitleStyled>
    <SimpleModalTitleSpan>{title}</SimpleModalTitleSpan>
    {badgeTitle && (
      <BadgeStyled $colour={badgeColour}>{badgeTitle}</BadgeStyled>
    )}
  </SimpleModalTitleStyled>
);
